import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { apiFetch, apiPut } from "@/lib/api";
import type { DocStatus, RollupStatus } from "@/lib/compliance-status";
import { complianceKeys } from "@/lib/query";

// ── Response shapes ────────────────────────────────────────────────────────

/**
 * A current compliance document row. Field names mirror the DB column names
 * exactly (e.g. `expiryDate`, not `expirationDate`). The UI composes
 * "missing" slots by cross-referencing a company's requirements against
 * this list — the server does not return placeholder rows.
 */
export interface ComplianceDocument {
  id: string;
  companyId: string;
  companyName?: string;
  companyType?: string;
  documentType: string;
  documentTypeId: string | null;
  status: string | null;
  issueDate: string | null;
  expiryDate: string | null;
  fileName: string | null;
  mimeType: string | null;
  fileSize: number | null;
  storagePath: string | null;
  uploadedBy: string | null;
  uploadedAt: string | null;
  notes: string | null;
  version: number;
  isCurrent: boolean;
  replacedById: string | null;
  replacedAt: string | null;
  deletedAt: string | null;
  deletedBy: string | null;
  deleteReason: string | null;
  createdAt: string;
  updatedAt: string;
}

export interface ComplianceDocumentType {
  id: string;
  code: string;
  label: string;
  requiresExpiry: boolean;
  isRetired: boolean;
}

export interface ComplianceRequirement {
  id: string;
  companyType: string;
  documentTypeId: string;
  isRequired: boolean;
}

export interface ComplianceRollup {
  overall: RollupStatus;
  perType: Record<string, DocStatus>;
  nextExpiry: string | null;
}

export interface ComplianceProfile {
  company: { id: string; companyName: string; type: string };
  types: ComplianceDocumentType[];
  requirements: ComplianceRequirement[];
  documents: ComplianceDocument[];
  rollup: ComplianceRollup;
}

// ── Reads ──────────────────────────────────────────────────────────────────

/**
 * The unified payload that drives the Compliance tab — types, requirements,
 * current documents, and a server-computed rollup all in one round trip.
 */
export function useComplianceProfile(companyId: string | undefined | null) {
  return useQuery({
    queryKey: complianceKeys.profile(companyId ?? ""),
    queryFn: () =>
      apiFetch<{ data: ComplianceProfile }>(
        `/compliance-documents/profile?companyId=${encodeURIComponent(companyId ?? "")}`,
      ).then((r) => r.data),
    enabled: !!companyId,
  });
}

/**
 * Flat list of current non-deleted docs. When `companyId` is passed, scoped
 * to that company. When omitted, returns all docs joined with companyName /
 * companyType — used by the top-level /compliance page.
 */
export function useComplianceDocuments(companyId?: string) {
  return useQuery({
    queryKey: complianceKeys.list(companyId ?? null),
    queryFn: () =>
      apiFetch<ComplianceDocument[]>(
        `/compliance-documents${companyId ? `?companyId=${encodeURIComponent(companyId)}` : ""}`,
      ),
  });
}

export function useComplianceHistory(companyId: string | null, documentTypeId: string | null) {
  return useQuery({
    queryKey: complianceKeys.history(companyId ?? "", documentTypeId ?? ""),
    queryFn: () =>
      apiFetch<{ data: ComplianceDocument[] }>(
        `/compliance/history?companyId=${encodeURIComponent(companyId ?? "")}&documentTypeId=${encodeURIComponent(documentTypeId ?? "")}`,
      ).then((r) => r.data),
    enabled: !!companyId && !!documentTypeId,
  });
}

// ── Mutations ──────────────────────────────────────────────────────────────
//
// Upload/replace bypass `apiPost` because that helper hardcodes JSON headers.
// For FormData we let the browser set the multipart boundary, matching the
// convention in `src/hooks/use-attachments.ts`.

export interface UploadComplianceInput {
  companyId: string;
  documentTypeId: string;
  file: File;
  issueDate?: string | null;
  expiryDate?: string | null;
  notes?: string | null;
}

function buildUploadFormData(input: {
  file: File;
  companyId?: string;
  documentTypeId?: string;
  issueDate?: string | null;
  expiryDate?: string | null;
  notes?: string | null;
}): FormData {
  const fd = new FormData();
  fd.set("file", input.file);
  if (input.companyId) fd.set("companyId", input.companyId);
  if (input.documentTypeId) fd.set("documentTypeId", input.documentTypeId);
  if (input.issueDate) fd.set("issueDate", input.issueDate);
  if (input.expiryDate) fd.set("expiryDate", input.expiryDate);
  if (input.notes) fd.set("notes", input.notes);
  return fd;
}

async function postMultipart<T>(path: string, fd: FormData): Promise<T> {
  const res = await fetch(`/api${path}`, { method: "POST", body: fd });
  if (!res.ok) {
    const err = await res.json().catch(() => ({ error: `Upload failed: ${res.status}` }));
    throw new Error((err as { error?: string }).error ?? `Upload failed: ${res.status}`);
  }
  return res.json() as Promise<T>;
}

async function deleteWithReason(path: string, reason: string): Promise<void> {
  const res = await fetch(`/api${path}`, {
    method: "DELETE",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ reason }),
  });
  if (!res.ok && res.status !== 204) {
    const err = await res.json().catch(() => ({ error: `Delete failed: ${res.status}` }));
    throw new Error((err as { error?: string }).error ?? `Delete failed: ${res.status}`);
  }
}

export function useUploadComplianceDocument() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: UploadComplianceInput) =>
      postMultipart<{ data: ComplianceDocument }>(
        "/compliance-documents",
        buildUploadFormData(input),
      ),
    onSuccess: (_data, vars) => {
      qc.invalidateQueries({ queryKey: complianceKeys.profile(vars.companyId) });
      // Invalidate the entire compliance namespace so the no-arg /compliance
      // list (key `["compliance","list",null]`) also refreshes alongside the
      // company-specific list.
      qc.invalidateQueries({ queryKey: complianceKeys.all });
      toast.success("Compliance document uploaded");
    },
    onError: (err: Error) => toast.error(err.message),
  });
}

export interface ReplaceComplianceInput {
  currentId: string;
  companyId: string;
  file: File;
  issueDate?: string | null;
  expiryDate?: string | null;
  notes?: string | null;
}

export function useReplaceComplianceDocument() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: ReplaceComplianceInput) => {
      const fd = buildUploadFormData({
        file: input.file,
        issueDate: input.issueDate,
        expiryDate: input.expiryDate,
        notes: input.notes,
      });
      return postMultipart<{ data: ComplianceDocument }>(
        `/compliance-documents/${input.currentId}/replace`,
        fd,
      );
    },
    onSuccess: (_data, vars) => {
      qc.invalidateQueries({ queryKey: complianceKeys.profile(vars.companyId) });
      qc.invalidateQueries({ queryKey: complianceKeys.all });
      qc.invalidateQueries({
        queryKey: complianceKeys.history(vars.companyId, ""),
        exact: false,
      });
      toast.success("Compliance document replaced");
    },
    onError: (err: Error) => toast.error(err.message),
  });
}

export function useUpdateComplianceDocument() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({
      id,
      ...data
    }: {
      id: string;
      companyId?: string;
      issueDate?: string | null;
      expiryDate?: string | null;
      notes?: string | null;
    }) => apiPut<{ data: ComplianceDocument }>(`/compliance-documents/${id}`, data),
    onSuccess: (_data, vars) => {
      if (vars.companyId) {
        qc.invalidateQueries({ queryKey: complianceKeys.profile(vars.companyId) });
      }
      qc.invalidateQueries({ queryKey: complianceKeys.all });
      toast.success("Compliance document updated");
    },
    onError: (err: Error) => toast.error(err.message),
  });
}

export function useSoftDeleteComplianceDocument() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, reason }: { id: string; reason: string; companyId: string }) =>
      deleteWithReason(`/compliance-documents/${id}`, reason),
    onSuccess: (_data, vars) => {
      qc.invalidateQueries({ queryKey: complianceKeys.profile(vars.companyId) });
      qc.invalidateQueries({ queryKey: complianceKeys.all });
      toast.success("Compliance document deleted");
    },
    onError: (err: Error) => toast.error(err.message),
  });
}
